import { useState, useEffect, useRef, useCallback } from "react";
import mqtt from "mqtt";

const MQTT_BROKER_URL =
  "wss://ag5cqau3pn76b-ats.iot.eu-west-2.amazonaws.com/mqtt?x-amz-customauthorizer-name=DashboardAuthorizer";
const TELEMETRY_TOPIC_SUFFIX = "/TELEMETRY";

const useMqttTelemetry = (deviceId, shareCode, onStreamRevoked) => {
  const [telemetryData, setTelemetryData] = useState({});
  const [mqttError, setMqttError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isRevoked, setIsRevoked] = useState(false);
  const clientRef = useRef(null);
  const onStreamRevokedRef = useRef(onStreamRevoked);

  // Keep callback ref updated
  useEffect(() => {
    onStreamRevokedRef.current = onStreamRevoked;
  }, [onStreamRevoked]);

  useEffect(() => {
    if (!deviceId) {
      console.log("MQTT Hook: No deviceId provided, skipping connection.");
      // Clear previous data if deviceId becomes null/undefined
      setTelemetryData({});
      setIsConnected(false);
      if (clientRef.current) {
        clientRef.current.end(true);
        clientRef.current = null;
      }
      return;
    }

    const telemetryTopic = `${deviceId}${TELEMETRY_TOPIC_SUFFIX}`;
    const revocationTopic = shareCode ? `stream-revoked/${shareCode}` : null;
    console.log(
      `MQTT Hook: Attempting to connect to ${MQTT_BROKER_URL} for device ${deviceId}`
    );

    // Ensure previous client is disconnected before creating a new one
    if (clientRef.current) {
      clientRef.current.end(true);
    }

    const client = mqtt.connect(MQTT_BROKER_URL);
    clientRef.current = client;

    client.on("connect", () => {
      console.log("MQTT Hook: Connected to broker");
      setIsConnected(true);
      setMqttError(null);

      // Subscribe to telemetry
      client.subscribe(telemetryTopic, (err) => {
        if (err) {
          console.error("MQTT Hook: Telemetry subscription error:", err);
          setMqttError(`Failed to subscribe to ${telemetryTopic}`);
        } else {
          console.log(`MQTT Hook: Subscribed to ${telemetryTopic}`);
        }
      });

      // Subscribe to revocation topic (specific to this share code)
      if (revocationTopic) {
        client.subscribe(revocationTopic, (err) => {
          if (err) {
            console.error("MQTT Hook: Revocation subscription error:", err);
          } else {
            console.log(`MQTT Hook: Subscribed to ${revocationTopic}`);
          }
        });
      }
    });

    client.on("message", (receivedTopic, message) => {
      try {
        const payload = JSON.parse(message.toString());

        // Handle revocation message (specific to this share code)
        if (revocationTopic && receivedTopic === revocationTopic) {
          console.log(
            "MQTT Hook: Stream REVOKED for this share code!",
            payload
          );
          setIsRevoked(true);
          if (onStreamRevokedRef.current) {
            onStreamRevokedRef.current(payload);
          }
          return;
        }

        // Handle telemetry message
        if (receivedTopic === telemetryTopic) {
          setTelemetryData(payload);
        }
      } catch (e) {
        console.error("MQTT Hook: Error parsing message JSON:", e);
        setMqttError("Error parsing data.");
      }
    });

    client.on("error", (err) => {
      console.error("MQTT Hook: Connection error:", err);
      setMqttError("MQTT connection error.");
      setIsConnected(false);
    });

    client.on("close", () => {
      console.log("MQTT Hook: Connection closed");
      setIsConnected(false);
    });

    client.on("offline", () => {
      console.log("MQTT Hook: Client offline");
      setIsConnected(false);
    });

    client.on("reconnect", () => {
      console.log("MQTT Hook: Reconnecting...");
    });

    return () => {
      if (clientRef.current) {
        console.log(
          "MQTT Hook: Cleaning up. Unsubscribing and disconnecting..."
        );
        try {
          if (clientRef.current.connected) {
            clientRef.current.unsubscribe(telemetryTopic);
            if (revocationTopic) {
              clientRef.current.unsubscribe(revocationTopic);
            }
          }
        } catch (err) {
          console.warn(
            "MQTT Hook: Error during unsubscribe (non-critical):",
            err
          );
        }

        try {
          clientRef.current.end(true);
        } catch (err) {
          console.warn(
            "MQTT Hook: Error during disconnect (non-critical):",
            err
          );
        }

        clientRef.current = null;
        setIsConnected(false);
      }
    };
  }, [deviceId, shareCode]);

  return { telemetryData, isConnected, mqttError, isRevoked };
};

export default useMqttTelemetry;
